import React, { useEffect, useState } from 'react';
import api from '../../services/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { useToast } from '../../hooks/use-toast';
import { DollarSign, Calendar, CheckCircle, AlertCircle } from 'lucide-react';
import { Skeleton } from '../../components/ui/skeleton';
import { Badge } from '../../components/ui/badge';

const Loans = () => {
  const [loans, setLoans] = useState([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    fetchLoans();
  }, []);

  const fetchLoans = async () => {
    try {
      const response = await api.get('/me/loans');
      setLoans(response.data);
    } catch (error) {
      console.error('Error fetching loans:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  const getStatusBadge = (status) => {
    const variants = {
      OFFERED: 'secondary',
      ACTIVE: 'default',
      PAID: 'outline',
      DEFAULTED: 'destructive',
    };

    const labels = {
      OFFERED: 'Ofertado',
      ACTIVE: 'Ativo',
      PAID: 'Pago',
      DEFAULTED: 'Inadimplente',
    };

    return (
      <Badge variant={variants[status] || 'secondary'}>
        {labels[status] || status}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="space-y-8">
        <div>
          <h1 className="text-4xl font-bold tracking-tight">Empréstimos</h1>
          <p className="text-muted-foreground mt-2">Seus empréstimos e financiamentos</p>
        </div>
        <div className="space-y-4">
          {[1, 2].map((i) => (
            <Card key={i}>
              <CardHeader>
                <Skeleton className="h-6 w-48" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-24 w-full" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  if (loans.length === 0) {
    return (
      <div className="space-y-8" data-testid="loans-page">
        <div>
          <h1 className="text-4xl font-bold tracking-tight">Empréstimos</h1>
          <p className="text-muted-foreground mt-2">Seus empréstimos e financiamentos</p>
        </div>
        <Card className="border-border/50">
          <CardContent className="py-12 text-center">
            <DollarSign className="mx-auto h-12 w-12 text-muted-foreground mb-4" strokeWidth={1.5} />
            <p className="text-muted-foreground">Você não possui empréstimos ativos.</p>
            <p className="text-sm text-muted-foreground mt-2">Entre em contato com seu gerente para simulações.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="loans-page">
      <div>
        <h1 className="text-4xl font-bold tracking-tight">Empréstimos</h1>
        <p className="text-muted-foreground mt-2">Seus empréstimos e financiamentos</p>
      </div>

      <div className="space-y-6">
        {loans.map((loan) => (
          <Card key={loan.id} className="border-border/50" data-testid={`loan-${loan.id}`}>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <DollarSign className="h-5 w-5" strokeWidth={1.5} />
                  Empréstimo #{loan.id}
                </CardTitle>
                {getStatusBadge(loan.status)}
              </div>
              <CardDescription>
                Contratado em {formatDate(loan.created_at)}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid gap-6 md:grid-cols-3">
                <div className="rounded-lg border border-border/50 p-4">
                  <p className="text-sm text-muted-foreground mb-2">Valor Principal</p>
                  <p className="text-2xl font-bold font-mono">
                    {formatCurrency(loan.principal)}
                  </p>
                </div>
                <div className="rounded-lg border border-border/50 p-4">
                  <p className="text-sm text-muted-foreground mb-2">Taxa de Juros</p>
                  <p className="text-2xl font-bold font-mono">
                    {(loan.interest_rate_monthly * 100).toFixed(2)}%
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">ao mês</p>
                </div>
                <div className="rounded-lg border border-border/50 p-4">
                  <p className="text-sm text-muted-foreground mb-2">Prazo</p>
                  <p className="text-2xl font-bold font-mono">
                    {loan.term_months}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">meses</p>
                </div>
              </div>

              {loan.status === 'ACTIVE' && (
                <div className="mt-6 rounded-lg bg-muted/50 p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
                      <span className="text-sm text-muted-foreground">
                        Desembolsado em {formatDate(loan.disbursed_at)}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default Loans;